import { Action_Context_Init, Action_Context_DataUpdated } from './../actions/context.actions';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { map, switchMap, tap, catchError, take } from 'rxjs/operators';
import { Observable, of, from, combineLatest } from 'rxjs';

import { AuthenticationService } from './../../services/authentication.service';

import * as fromActions from '../actions/authentication.actions';

import { ApiService } from '../../services/api.service';
import { UtilService } from 'src/app/core/services/util.service';

import { User, UserDetails } from 'src/app/models/Users';
import { StoreAction } from '../../models/rootState.model';
import { Credential } from '../../models/authentication.model';
import { selectContextEntity } from '../selectors/context.selectors';
import { selectAuthUserPermissions } from '../selectors/authentication.selectors';
import { Entity } from 'src/app/models/entities.model';
import { FingerprintService } from '../../services/fingerprint.service';
import { environment } from 'src/environments/environment';
import { Athlete } from 'src/app/models/athletes.model';
import { Club } from 'src/app/models/clubs.model';
import { Contributor } from 'src/app/features/contributor/models/contributor.model';
import { League } from 'src/app/models/leagues.model';
import { Referee } from 'src/app/models/referee.model';
import { TEnvironment } from 'src/environments/environment.model';
import { EntityService } from 'src/app/features/entity/services/entity.service';
import { CacheApi } from 'src/app/features/cache/cache-api';
import { BrowserPreference, BrowserPreferencesService } from '../../services/browser-preferences.service';
import { User as FBUser } from '@angular/fire/auth';

@Injectable()
export class AuthEffects {

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private router: Router,
    private service: AuthenticationService,
    private util: UtilService,
    private store: Store<any>,
    private fingerprint: FingerprintService,
    private preferences: BrowserPreferencesService,
    private sEntity: EntityService
  ) { }


  @Effect()
  initialEffect$: Observable<Action> = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.LOAD_CURRENT_FIREBASE_USER),
    switchMap(() => {
      if (environment.environment == 'local') {
        let email = '';
        if (localStorage.getItem('fu_local')) {
          email = 'fakeuser' + '-' + localStorage.getItem('fu_local');
        } else {
          const loggedUser = localStorage.getItem('logged_email');
          const storedString = localStorage.getItem('localfbuser-' + (loggedUser));
          if (storedString) {
            const storedData = JSON.parse(storedString);
            if (storedData.user && storedData.token) {
              email = loggedUser;
            }
          }

        }

        if (email) {
          const data = {
            user: {
              email,
              emailVerified: true
            }
          } as any;
          return of(new fromActions.Action_Auth_LoadCurrentFirebaseUser_Success(data));
        }
      }



      return this.service.currentUser().pipe(
        map(user => {
          return user
            ? new fromActions.Action_Auth_LoadCurrentFirebaseUser_Success(user)
            : new fromActions.Action_Auth_LoadCurrentFirebaseUser_Empty();
        }),
        take(1)
      );

    })
  );


  @Effect()
  signout$: Observable<Action> = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.SIGNOUT),
    tap(() => { this.util.loader.show('Saindo do sistema...'); }),
    switchMap(() => this.service.signOut().pipe(
      map(() => new fromActions.Action_Auth_Signout_Success()),
      tap(() => {
        localStorage.removeItem('logged_email');
        const cache: CacheApi = new CacheApi();
        cache.invalidateGroups(['entity', 'user']);
        this.util.loader.close();
        this.router.navigate(['start']);
      }),
      catchError(err => of(new fromActions.Action_Auth_Error({
        error: err,
        message: 'Falha ao se desconectar do sistema',
        name: 'Signout Error'
      })))
    ))
  );

  @Effect()
  signin$: Observable<Action> = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.SIGNIN),
    switchMap((action: StoreAction<Credential>) => {
      // if (environment.environment == 'local') {
      //   const storedString = localStorage.getItem('localfbuser-' + (action.payload.email.toLowerCase()));
      //   if (storedString) {
      //     const storedUSer = JSON.parse(storedString);
      //     return of(new fromActions.Action_Auth_Signin_Firebase_Success(storedUSer.user));
      //   }
      // }

      return this.service.signInWithEmail(action.payload).pipe(
        map(res => {
          if (res.user.emailVerified == false && environment.environment.notInArray<TEnvironment>(['local'])) {
            return new fromActions.Action_Auth_EmailNotVerified(res.user);
          }

          if (environment.environment === 'local') {
            localStorage.setItem('logged_email', action.payload.email.toLowerCase());
            localStorage.setItem('localfbuser-' +action.payload.email.toLowerCase(), JSON.stringify({ user: res.user, token: '' }));
          }

          return new fromActions.Action_Auth_Signin_Firebase_Success(res.user);
        }),
        catchError((err: Error) => of(new fromActions.Action_Auth_Error({
          error: err,
          message: err.message || 'Falha ao efetuar o login',
          name: 'Login Action Error'
        })))

      );
    })
  );


  @Effect()
  getUserSigninData$: Observable<Action> = this.actions$.pipe(
    ofType(
      fromActions.AUTH_ACTION_TYPES.SIGNIN_FIREBASE_SUCCESS,
      fromActions.AUTH_ACTION_TYPES.SIGNIN_WITH_FACEBOOK_SUCCESS,
      fromActions.AUTH_ACTION_TYPES.SIGNIN_WITH_GOOGLE_SUCCESS
    ),
    switchMap((action: StoreAction<firebase.default.User>) => {
      this.fingerprint.init();
      const { fingerprint } = this.fingerprint.device_info;
      return action.payload
        ? this.api.post<User>('users/signin', { device_info: { fingerprint } }).pipe(
          map(user => {
            if (user.error) { return user; }
            user.payload = this.prepareUser(user.payload);
            return user;
          }),
          map(user => user.error
            ? new fromActions.Action_Auth_Error({
              error: new Error(user.error.message),
              message: 'Erro ao carregar usuário',
              name: 'Api Error'
            })
            : new fromActions.Action_Auth_RetrieveUserData_Success(user.payload)
          )
        )
        : of(new fromActions.Action_Auth_RetrieveUserData_Success(null));
    })
  );

  @Effect({ dispatch: false })
  getUserData$ = this.actions$.pipe(
    ofType(
      fromActions.AUTH_ACTION_TYPES.LOAD_CURRENT_FIREBASE_USER_SUCCESS
    ),
    switchMap((action: StoreAction<firebase.default.User>) => {
      if (!action.payload) {
        this.store.dispatch(new fromActions.Action_Auth_RetrieveUserData_Success(null));
        return;
      }

      return this.sEntity.getLoggedUserData({ silentIfCode: [0, 504], acceptCache: '2h', returnType: 'cached-and-api', cacheGroup: 'user' })
        .pipe(
          map(response => {
            if (response.error) { return response; }
            response.payload = this.prepareUser(response.payload);
            return response;
          }),
          map(user => {
            if (user.error) {
              this.store.dispatch(
                new fromActions.Action_Auth_Error({
                  error: new Error(user.error.message),
                  message: 'Erro ao carregar usuário',
                  name: 'Api Error'
                })
              );
            } else {
              this.store.dispatch(new fromActions.Action_Auth_RetrieveUserData_Success(user.payload as User));
            }
            return user;
          }),
          catchError(async (err) => {

            // verify last successfully user stored data
            const stored = await this.preferences.get<User>(BrowserPreference.LAST_LOGGED_USER);
            if (stored && stored.email == action.payload.email) {
              this.store.dispatch(
                new fromActions.Action_Auth_RetrieveUserData_Success(stored)
              );
              return;
            }

            this.store.dispatch(
              new fromActions.Action_Auth_RetrieveUserData_Success(null)
            );
          })
        );
    }),
    catchError((err) => {

      return of(new fromActions.Action_Auth_Error({
        error: new Error(err.message),
        message: 'Erro ao carregar os dados do usuário',
        name: 'Api Error'
      }));
    })
  );


  @Effect()
  EntityAdded$: Observable<Action> = this.actions$.pipe(
    ofType(
      fromActions.AUTH_ACTION_TYPES.USER_DATA_ENTITY_ADDED
    ),
    switchMap((action: StoreAction<firebase.default.User>) =>
      this.sEntity.getLoggedUserData().pipe(
        map(user => {
          if (user.error) {
            return new fromActions.Action_Auth_Error({
              error: new Error(user.error.message),
              message: 'Erro ao carregar usuário',
              name: 'Api Error'
            });
          }
          return new fromActions.Action_Auth_RetrieveUserData_Success(user.payload);
        }))
    ),
    catchError((err) => {
      return of(new fromActions.Action_Auth_Error({
        error: new Error(err.message),
        message: 'Erro ao carregar os dados do usuário',
        name: 'Api Error'
      }));
    })
  );

  @Effect()
  ProfileUpdated$: Observable<Action> = this.actions$.pipe(
    ofType(
      fromActions.AUTH_ACTION_TYPES.USER_DATA_UPDATED_BY_FORM,
      fromActions.AUTH_ACTION_TYPES.RELOAD
    ),
    switchMap((action: StoreAction<firebase.default.User>) =>
      this.sEntity.getLoggedUserData().pipe(
        map(user => {
          if (user.error) {
            return new fromActions.Action_Auth_Error({
              error: new Error(user.error.message),
              message: 'Erro ao carregar usuário',
              name: 'Api Error'
            });
          }
          return new fromActions.Action_Auth_UserDataReloaded(user.payload);
        }))
    ),
    catchError((err) => {
      return of(new fromActions.Action_Auth_Error({
        error: new Error(err.message),
        message: 'Erro ao carregar os dados do usuário',
        name: 'Api Error'
      }));
    })
  );

  @Effect({ dispatch: false })
  ReloadEntity$ = this.actions$.pipe(
    ofType(
      fromActions.AUTH_ACTION_TYPES.USER_DATA_RELOADED
    ),
    map(() => {
      const entities$ = this.store.select(selectAuthUserPermissions).pipe(take(1));
      const context$ = this.store.select(selectContextEntity).pipe(take(1));
      combineLatest([entities$, context$])
        .pipe(take(1))
        .subscribe(([entities, context]) => {
          const groups: Entity[] = entities[context.source_name];
          const entity = groups.find(i => i.id === context.id);
          if (entity) {
            this.store.dispatch(new Action_Context_DataUpdated(entity));
          }
        });
    }),
    catchError((err) => {
      return of(new fromActions.Action_Auth_Error({
        error: new Error(err.message),
        message: 'Erro ao carregar os dados do usuário',
        name: 'Api Error'
      }));
    })
  );


  @Effect()
  updateSignedInData$: Observable<Action> = this.actions$.pipe(
    ofType(
      fromActions.AUTH_ACTION_TYPES.USER_DATA_UPDATED_BY_STARTUP
    ),
    map((action: StoreAction) =>
      new fromActions.Action_Auth_RetrieveUserData_Success(action.payload)
    )
  );

  @Effect()
  signup$: Observable<Action> = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.SIGNUP),
    switchMap((action: StoreAction<Credential>) => this.service.createAuthUser(action.payload)),
    catchError((err: Error) => of(new fromActions.Action_Auth_Error({
      error: err,
      message: err.message || 'Falha ao efetuar o login',
      name: 'Auth Error'
    }))),
    switchMap((res) => {
      if (res instanceof fromActions.Action_Auth_Error) {
        return of(res);
      }
      return this.service.sendEmailVerification().pipe(
        map(() => new fromActions.Action_Auth_Signup_Success()),
        catchError((err: Error) => of(new fromActions.Action_Auth_Error({
          error: err,
          message: err.message || 'Falha ao efetuar o login',
          name: ' Auth Error'
        })))
      );
    })
  );

  @Effect()
  emailNotVerified$ = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.EMAIL_NOT_VERIFIED),
    switchMap(() => from(this.util.dialog.confirm(`Seu endereço de email ainda não foi verificado.
        <br /> Deseja receber um novo email de verificação?`, 'Conta não verificada.'))),
    switchMap((confirm) => {
      if (confirm) {
        return this.service.sendEmailVerification().pipe(
          tap(() => {
            this.store.dispatch(new fromActions.Action_Auth_ValidationEmailSent());
            this.util.toast.success('e-mail de confirmação enviado com sucesso');
          })
        );
      } else {
        this.store.dispatch(new fromActions.Action_Auth_ValidationEmailSkip());
        return of(null);
      }
    }),
    map(sent => new fromActions.Action_Auth_Signout())
  );

  @Effect({ dispatch: false })
  SigninWithFacebook$: Observable<Action> = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.SIGNIN_WITH_FACEBOOK),
    tap(() => {
      this.service.signInFacebook()
        .pipe(take(1))
        .subscribe(
          result => {
            this.store.dispatch(new fromActions.Action_Auth_SigninWithFacebook_Success(result.user));
          },
          (err) => {
            this.store.dispatch(new fromActions.Action_Auth_Error({
              error: new Error('Falha ao efetuar o login'),
              message: err.message || 'Falha ao efetuar o login',
              name: 'Login Action Error'
            }));

          });
    })
  );

  @Effect()
  SigninWithGoogle$: Observable<Action> = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.SIGNIN_WITH_GOOGLE),
    switchMap(() => this.service.signInGoogle().pipe(
      map((result) => new fromActions.Action_Auth_SigninWithFacebook_Success(result.user)),
      catchError((err: string) => of(new fromActions.Action_Auth_Error({
        error: new Error(err),
        message: err || 'Falha ao efetuar o login',
        name: 'Login Action Error'
      })))
    ))
  );

  @Effect({ dispatch: false })
  userDataChanged$ = this.actions$.pipe(
    ofType(fromActions.AUTH_ACTION_TYPES.RETRIEVE_LOGGED_USER_DATA_SUCCESS),
    tap((action: StoreAction<User>) => {
      this.store.dispatch(new Action_Context_Init(action.payload));
      this.preferences.set(BrowserPreference.LAST_LOGGED_USER, action.payload);
    })
  );


  prepareUserDetails(user: UserDetails): User {

    const response: User = user.profile;

    if (user.permissions && user.entities) {
      user.permissions.forEach(permission => {
        permission.target = user.entities.find(e => +e.id == +permission.target_id);
      });
    }

    if (user.permissions) {
      response.roles = user.permissions;
      response.permissions = {} as any;
      response.permissions.athletes = user.permissions.filter(p => p.target.source_name == 'athletes')?.map(p => new Athlete(p.target));


    }



    // if (user.permissions.athletes) {
    //   user.permissions.athletes = user.permissions.athletes.map(a => new Athlete(a));
    // }

    // if (user.permissions.clubs) {
    //   user.permissions.clubs = user.permissions.clubs.map(a => new Club(a));
    // }

    // if (user.permissions.contributors) {
    //   user.permissions.contributors = user.permissions.contributors.map(a => new Contributor(a));
    // }

    // if (user.permissions.leagues) {
    //   user.permissions.leagues = user.permissions.leagues.map(a => new League(a));
    // }

    // if (user.permissions.referees) {
    //   user.permissions.referees = user.permissions.referees.map(a => new Referee(a));
    // }

    return response;
  }

  prepareUser(user: User): User {
    if (user.permissions.athletes) {
      user.permissions.athletes = user.permissions.athletes.map(a => new Athlete(a));
    }

    if (user.permissions.clubs) {
      user.permissions.clubs = user.permissions.clubs.map(a => new Club(a));
    }

    if (user.permissions.contributors) {
      user.permissions.contributors = user.permissions.contributors.map(a => new Contributor(a));
    }

    if (user.permissions.leagues) {
      user.permissions.leagues = user.permissions.leagues.map(a => new League(a));
    }

    if (user.permissions.referees) {
      user.permissions.referees = user.permissions.referees.map(a => new Referee(a));
    }

    return user;
  }

}
